import React, { useState, useRef } from 'react';
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle, X, Download, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

export default function BatchUploadModal({ open, onOpenChange, title, description, templateColumns, templateFileName, onUpload }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleClose = () => {
    setFile(null);
    setResult(null);
    setUploading(false);
    onOpenChange(false);
  };

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0];
    if (selected) {
      const ext = selected.name.toLowerCase();
      if (!ext.endsWith('.csv') && !ext.endsWith('.xlsx') && !ext.endsWith('.xls')) {
        return;
      }
      setFile(selected);
      setResult(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) {
      const ext = dropped.name.toLowerCase();
      if (ext.endsWith('.csv') || ext.endsWith('.xlsx') || ext.endsWith('.xls')) {
        setFile(dropped);
        setResult(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const data = await onUpload(file);
      setResult(data);
    } catch (err) {
      setResult({ error: err?.response?.data?.detail || err.message || 'Error al procesar archivo' });
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const BOM = '\uFEFF';
    const csvContent = BOM + templateColumns.join(',') + '\n';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = templateFileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-slate-500">{description}</p>

          {/* Download template */}
          <button
            onClick={downloadTemplate}
            className="flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium transition-colors"
            data-testid="download-template-btn"
          >
            <Download className="w-4 h-4" />
            Descargar plantilla CSV
          </button>

          {/* Upload zone */}
          {!result && (
            <>
              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
                  dragOver ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'
                }`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                data-testid="batch-upload-dropzone"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="batch-upload-input"
                />
                <Upload className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm font-medium text-slate-700">
                  {file ? file.name : 'Arrastra tu archivo aquí o haz clic para seleccionar'}
                </p>
                <p className="text-xs text-slate-400 mt-1">Formatos: CSV, Excel (.xlsx)</p>
              </div>

              {file && (
                <div className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-3">
                  <div className="flex items-center gap-2">
                    <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm font-medium text-slate-700 truncate max-w-[250px]">{file.name}</span>
                    <span className="text-xs text-slate-400">({(file.size / 1024).toFixed(1)} KB)</span>
                  </div>
                  <button onClick={() => { setFile(null); }} className="text-slate-400 hover:text-slate-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </>
          )}

          {/* Results */}
          {result && !result.error && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-slate-700">{result.total_processed}</p>
                  <p className="text-xs text-slate-500">Procesados</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-green-600">{result.created}</p>
                  <p className="text-xs text-green-600">Creados</p>
                </div>
                <div className={`rounded-lg p-3 text-center ${result.errors_count > 0 ? 'bg-red-50' : 'bg-slate-50'}`}>
                  <p className={`text-xl font-bold ${result.errors_count > 0 ? 'text-red-600' : 'text-slate-400'}`}>{result.errors_count}</p>
                  <p className={`text-xs ${result.errors_count > 0 ? 'text-red-600' : 'text-slate-400'}`}>Errores</p>
                </div>
              </div>

              {result.created > 0 && (
                <div className="flex items-center gap-2 bg-green-50 text-green-700 rounded-lg px-4 py-2 text-sm">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  {result.created} registro(s) creado(s) exitosamente
                </div>
              )}

              {result.errors_count > 0 && (
                <div className="bg-red-50 rounded-lg p-3">
                  <p className="text-sm font-medium text-red-700 mb-2 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" /> Errores encontrados:
                  </p>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {result.errors.map((err, i) => (
                      <p key={i} className="text-xs text-red-600">
                        Fila {err.row}: {err.error}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {result?.error && (
            <div className="flex items-center gap-2 bg-red-50 text-red-700 rounded-lg px-4 py-3 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {result.error}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          {result ? (
            <Button onClick={handleClose} className="bg-emerald-600 hover:bg-emerald-700" data-testid="batch-close-btn">
              Cerrar
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancelar
              </Button>
              <Button
                onClick={handleUpload}
                disabled={!file || uploading}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="batch-upload-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Subir y Crear
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
